/**
 * SharedHorses.jsx — Chevaux partagés avec moi
 * Style identique à HorseList — mêmes styles, mêmes constantes, même structure.
 */
import { BTN } from '../constants/theme';
import { semanticColors } from '../constants/semanticColors';
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { authFetch } from '../utils/authFetch';
import { buildHorseSubtitle } from '../utils/horseSubtitle';
import { FileText, BarChart3, Radio, ClipboardList, LogOut, Pencil, Upload } from 'lucide-react';
import HorseIcon from '../shared/HorseIcon';
import ProtectedImage from '../shared/ProtectedImage';

const PHOTO_SIZE = 72;

const SharedHorses = ({ darkMode = false, onViewSharedHorse, onViewSessions, onViewLive, onViewHealth, onEdit, onUploadCSV }) => {
  const { t } = useTranslation();
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [leaveConfirm, setLeaveConfirm] = useState(null); // { shareId, horseName }

  // ── Styles identiques à HorseList ──────────────────────────────────────
  const cardStyle = {
    background: darkMode ? '#1E1E32' : '#fff',
    padding: '30px',
    borderRadius: '8px',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e0e0e0',
    boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '20px'
  };

  const horseCardStyle = {
    background: darkMode ? '#282845' : '#f8f9fa',
    padding: '16px',
    borderRadius: '8px',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e0e0e0',
    marginBottom: '12px'
  };

  const photoStyle = {
    width: PHOTO_SIZE, height: PHOTO_SIZE,
    borderRadius: '50%',
    border: darkMode ? '2px solid rgba(255,255,255,0.2)' : '2px solid #e0e0e0',
    flexShrink: 0, objectFit: 'cover'
  };

  const avatarStyle = {
    ...photoStyle,
    background: darkMode ? '#282845' : '#e8f0fe',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  // Styles boutons — voir src/constants/theme.js BTN.*

  // Libellés rôle réutilisés du namespace transverse roles.* (vague 1.2).
  const ROLE_COLOR = { veterinaire: '#059669', personne_confiance: '#D97706' };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch('/api/shared/horses');
        if (!res) return; // déconnexion automatique si 401
        const data = await res.json();
        if (data.success) setHorses(data.horses);
      } catch {
        setLoadError(t('horses.shared.loadError'));
      } finally { setLoading(false); }
    };
    load();
    // Chargement au mount uniquement ; t n'est lu que pour le message
    // d'erreur (l'ajouter re-fetcherait au changement de langue).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askLeave = (shareId, horseName, e) => {
    e.stopPropagation();
    setLeaveConfirm({ shareId, horseName });
  };

  const confirmLeave = async () => {
    const { shareId } = leaveConfirm;
    setLeaveConfirm(null);
    try {
      const res = await authFetch(`/api/shares/${shareId}`, { method: 'DELETE' });
      if (!res) return;
      const data = await res.json();
      if (data.success) setHorses(prev => prev.filter(h => h.shareId !== shareId));
    } catch {
      alert(t('horses.shared.leaveError'));
    }
  };

  if (loading) return null;
  if (loadError) return (
    <div style={{ padding: '12px 16px', background: '#FEF2F2', borderRadius: '8px',
      border: '1px solid #FECACA', color: semanticColors.redText, fontSize: '13px', marginBottom: '16px' }}>
      {loadError}
    </div>
  );
  if (horses.length === 0) return null;

  return (
    <div style={cardStyle}>
      {/* Modal confirmation quitter */}
      {leaveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: darkMode ? '#1E1E32' : '#fff', borderRadius: '12px',
            padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: '700',
              color: darkMode ? '#E1E1E1' : '#2C3E50' }}>
              {t('horses.shared.leaveTitle')}
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: darkMode ? '#B8BFC7' : '#6B7280', lineHeight: 1.6 }}>
              <Trans i18nKey="horses.shared.leaveMessage" values={{ name: leaveConfirm.horseName }} components={{ strong: <strong /> }} />
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setLeaveConfirm(null)}
                style={{ padding: '9px 18px', borderRadius: '8px', border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #ddd',
                  background: 'none', color: darkMode ? '#E1E1E1' : '#2C3E50', cursor: 'pointer', fontSize: '14px' }}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmLeave}
                style={{ padding: '9px 18px', borderRadius: '8px', border: 'none',
                  background: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                {t('horses.shared.leaveConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      <h2 style={{ color: '#1B77F1', marginBottom: '20px', fontSize: '20px' }}>
        {t('horses.shared.title')}
      </h2>

      {horses.map(h => {
        const subtitle = buildHorseSubtitle(h);
        const subtitleWithOwner = h.ownerName
          ? `${subtitle ? subtitle + ' · ' : ''}${t('horses.shared.owner', { name: h.ownerName })}`
          : subtitle;
        return (
        <div key={h._id} style={horseCardStyle}>
          {/* Ligne 1 : Photo + Infos — identique à HorseList */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            {h.photo1 ? (
              <ProtectedImage
                src={`/uploads/horses/${h.photo1}`}
                alt={h.horseName}
                style={photoStyle}
                horseId={h._id}
                slot="photo1"
                thumb
              />
            ) : (
              <div style={avatarStyle}>
                <HorseIcon size={40} color={darkMode ? '#90CAF9' : '#1B77F1'} />
              </div>
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Nom + badge rôle sur la même ligne */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                <h3 style={{
                  color: darkMode ? '#E1E1E1' : '#2C3E50',
                  margin: 0, fontSize: '17px', fontWeight: '600',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {h.horseName}
                </h3>
                <span style={{
                  padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                  fontWeight: '600', color: '#fff', flexShrink: 0,
                  background: ROLE_COLOR[h.shareRole] || '#D97706'
                }}>
                  {t(`roles.${h.shareRole}`, { defaultValue: h.shareRole })}
                </span>
              </div>
              {/* Métadonnées — même style que HorseList */}
              {subtitleWithOwner && (
                <p style={{
                  color: darkMode ? '#E8EDF2' : '#4A5568',
                  fontSize: '13px', margin: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {subtitleWithOwner}
                </p>
              )}
            </div>
          </div>

          {/* Ligne 2 : Boutons — même grille que HorseList */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
            <button type="button" style={BTN.primary}
              onClick={() => onViewSharedHorse && onViewSharedHorse(h._id, h.shareRole, h.acceptedAt)}>
              <FileText size={15} /> {t('horses.shared.actions.view')}
            </button>
            <button type="button" style={BTN.secondary(darkMode)}
              onClick={() => onViewSessions && onViewSessions(h._id, h.shareRole, h.acceptedAt)}>
              <BarChart3 size={15} /> {t('horses.shared.actions.data')}
            </button>
            <button type="button" style={BTN.secondary(darkMode)}
              onClick={() => onUploadCSV && onUploadCSV(h._id, h.shareRole, h.acceptedAt)}>
              <Upload size={15} /> {t('horses.shared.actions.upload')}
            </button>
            {h.equipmentId && (
              <button type="button" style={{ ...BTN.primary, background: '#10B981' }}
                onClick={() => onViewLive && onViewLive(h)}>
                <Radio size={15} /> {t('horses.shared.actions.live')}
              </button>
            )}
            {h.shareRole && (
              <button type="button" style={BTN.secondary(darkMode)}
                onClick={() => onEdit && onEdit(h)}>
                <Pencil size={15} /> {t('common.edit')}
              </button>
            )}
            <button type="button" style={BTN.secondary(darkMode)}
              onClick={() => onViewHealth && onViewHealth(h)}>
              <ClipboardList size={15} /> {t('horses.shared.actions.health')}
            </button>
            <button type="button" style={BTN.danger}
              onClick={(e) => askLeave(h.shareId, h.horseName, e)}>
              <LogOut size={15} /> {t('horses.shared.actions.leave')}
            </button>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default SharedHorses;
